import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { Route } from './+types/get-a-quote';
import { useNavigate, useSearchParams } from 'react-router';
import { buildMeta, breadcrumbSchema } from '~/lib/seo';
import { SITE } from '~/data/site';
import { formatPhoneInput, isValidPhone } from '@shared/utils';
import { fireConversion } from '~/lib/gtag';
import '~/styles/marketing/get-a-quote.css';

export const meta: Route.MetaFunction = () => {
  return buildMeta({
    title: `Get a Cleaning Quote | ${SITE.name} Tampa Bay`,
    description:
      'Get an instant cleaning quote for your Tampa Bay home. Enter square footage and service type — see live pricing in seconds. No obligations.',
    path: '/get-a-quote',
    jsonLd: breadcrumbSchema([
      { name: 'Home', url: `${SITE.url}/` },
      { name: 'Get a Quote', url: `${SITE.url}/get-a-quote` },
    ]),
  });
};

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type FurnishingState = 'furnished';
type ServiceType = 'one_time' | 'deep_clean' | 'recurring_maintenance';
type Recurrence = 'weekly' | 'biweekly' | 'every_4_weeks';

interface PricingApiResult {
  basePrice: number;
  upgradeAmount: number;
  totalPrice: number;
  estimatedHours: number;
  rateKey: string;
  rateUsed: number;
}

interface FormMessage {
  type: 'error';
  text: string;
}

const DEBOUNCE_MS = 500;
const SQFT_MIN = 200;
const SQFT_MAX = 15000;

const FURNISHING_OPTS: { value: FurnishingState; label: string }[] = [
  { value: 'furnished', label: 'Furnished' },
];
const SERVICE_OPTS: { value: ServiceType; label: string }[] = [
  { value: 'one_time', label: 'One-time' },
  { value: 'deep_clean', label: 'Deep clean' },
  { value: 'recurring_maintenance', label: 'Recurring' },
];
const RECURRENCE_OPTS: { value: Recurrence; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'every_4_weeks', label: 'Every 4 weeks' },
];

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function parseFurnishing(_v: string | null): FurnishingState {
  return 'furnished';
}

function parseService(v: string | null): ServiceType {
  if (v === 'deep_clean') return 'deep_clean';
  if (v === 'recurring_maintenance') return 'recurring_maintenance';
  return 'one_time';
}

function parseRecurrence(v: string | null): Recurrence {
  if (v === 'weekly') return 'weekly';
  if (v === 'every_4_weeks') return 'every_4_weeks';
  return 'biweekly';
}

function parseSqft(v: string | null): number {
  if (!v) return 1500;
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return 1500;
  return Math.max(SQFT_MIN, Math.min(SQFT_MAX, n));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function GetAQuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Prefill from query string (set by PricingCalculatorWidget CTA)
  const [sqft, setSqft] = useState<number>(() => parseSqft(searchParams.get('sqft')));
  const [furnishing, setFurnishing] = useState<FurnishingState>(() =>
    parseFurnishing(searchParams.get('furnishing')),
  );
  const [serviceType, setServiceType] = useState<ServiceType>(() =>
    parseService(searchParams.get('service')),
  );
  const [recurrence, setRecurrence] = useState<Recurrence>(() =>
    parseRecurrence(searchParams.get('frequency')),
  );
  const [includeDeepUpgrade, setIncludeDeepUpgrade] = useState<boolean>(
    () => searchParams.get('upgrade') === '1',
  );

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const [result, setResult] = useState<PricingApiResult | null>(null);
  const [loadingPrice, setLoadingPrice] = useState<boolean>(false);
  const [priceError, setPriceError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formMessage, setFormMessage] = useState<FormMessage | null>(null);

  const upgradeEligible = furnishing === 'furnished' && serviceType === 'recurring_maintenance';

  const payload = useMemo(
    () => ({
      sqft,
      furnishingState: furnishing,
      serviceType,
      ...(serviceType === 'recurring_maintenance' ? { recurrenceFrequency: recurrence } : {}),
      ...(upgradeEligible && includeDeepUpgrade ? { includeDeepUpgrade: true } : {}),
    }),
    [sqft, furnishing, serviceType, recurrence, includeDeepUpgrade, upgradeEligible],
  );

  useEffect(() => {
    if (sqft < SQFT_MIN || sqft > SQFT_MAX) return;
    const ctrl = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoadingPrice(true);
      setPriceError(null);
      try {
        const res = await fetch('/api/pricing/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
        });
        const data = (await res.json()) as { ok: boolean; data?: PricingApiResult; error?: string };
        if (!res.ok || !data.ok || !data.data) {
          setPriceError(data.error ?? 'Could not calculate price.');
          setResult(null);
        } else {
          setResult(data.data);
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setPriceError('Network error. Please try again.');
          setResult(null);
        }
      } finally {
        setLoadingPrice(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      ctrl.abort();
      window.clearTimeout(timer);
    };
  }, [payload, sqft]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormMessage(null);
    if (!name.trim() || !phone.trim()) {
      setFormMessage({ type: 'error', text: 'Please enter your name and phone number.' });
      return;
    }
    if (!isValidPhone(phone)) {
      setFormMessage({ type: 'error', text: 'Please enter a valid 10-digit phone number.' });
      return;
    }

    const messageLines = [
      `Home size: ${sqft} sq ft`,
      `Furnishing: ${furnishing}`,
      `Service: ${SERVICE_OPTS.find((o) => o.value === serviceType)?.label ?? serviceType}`,
      serviceType === 'recurring_maintenance'
        ? `Frequency: ${RECURRENCE_OPTS.find((o) => o.value === recurrence)?.label ?? recurrence}`
        : null,
      upgradeEligible && includeDeepUpgrade ? 'Deep clean upgrade: yes' : null,
      result ? `Estimated total: ${formatCents(result.totalPrice)}` : null,
      notes.trim() ? `Notes: ${notes.trim()}` : null,
    ]
      .filter((line): line is string => line !== null)
      .join('\n');

    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim(),
          message: messageLines,
          source: 'get-a-quote',
        }),
      });
      const data = (await res.json()) as { ok: boolean; data?: { message?: string }; error?: string };
      if (res.ok && data.ok) {
        fireConversion(); // on success, not on click — see thank-you.tsx
        void navigate('/thank-you');
      } else {
        setFormMessage({
          type: 'error',
          text: data?.error ?? `Something went wrong. Please email us at ${SITE.contact.email}.`,
        });
      }
    } catch {
      setFormMessage({
        type: 'error',
        text: `Failed to submit. Please try again or email us at ${SITE.contact.email}.`,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="gq-page" id="main-content">
      <section className="gq-hero">
        <span className="gq-hero__eyebrow">Instant Pricing</span>
        <h1 className="gq-hero__title">Get Your Quote</h1>
        <p className="gq-hero__lede">
          Enter your home size and pick a service &mdash; you&rsquo;ll see a live price from our current rates. No estimates that change later.
        </p>
      </section>

      <div className="gq-layout">
        <form className="gq-form" onSubmit={handleSubmit} noValidate>
          <h2 className="gq-form__title">Tell us about your home</h2>

          <div className="gq-form__field">
            <label className="gq-form__label" htmlFor="gq-sqft">Home size (sq ft)</label>
            <input
              id="gq-sqft"
              className="gq-form__input"
              type="number"
              min={SQFT_MIN}
              max={SQFT_MAX}
              step={50}
              value={sqft}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n)) setSqft(Math.max(SQFT_MIN, Math.min(SQFT_MAX, n)));
              }}
            />
          </div>

          <div className="gq-form__field">
            <span className="gq-form__label">Furnishing state</span>
            <div className="gq-form__radio-group" role="radiogroup" aria-label="Furnishing state">
              {FURNISHING_OPTS.map((opt) => (
                <div key={opt.value} className="gq-form__radio-item">
                  <input
                    className="gq-form__radio"
                    type="radio"
                    name="gq-furnishing"
                    id={`gq-f-${opt.value}`}
                    checked={furnishing === opt.value}
                    onChange={() => setFurnishing(opt.value)}
                  />
                  <label className="gq-form__radio-label" htmlFor={`gq-f-${opt.value}`}>{opt.label}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="gq-form__field">
            <span className="gq-form__label">Service type</span>
            <div className="gq-form__radio-group" role="radiogroup" aria-label="Service type">
              {SERVICE_OPTS.map((opt) => (
                <div key={opt.value} className="gq-form__radio-item">
                  <input
                    className="gq-form__radio"
                    type="radio"
                    name="gq-service"
                    id={`gq-s-${opt.value}`}
                    checked={serviceType === opt.value}
                    onChange={() => setServiceType(opt.value)}
                  />
                  <label className="gq-form__radio-label" htmlFor={`gq-s-${opt.value}`}>{opt.label}</label>
                </div>
              ))}
            </div>
          </div>

          {serviceType === 'recurring_maintenance' && (
            <div className="gq-form__field">
              <span className="gq-form__label">Frequency</span>
              <div className="gq-form__radio-group" role="radiogroup" aria-label="Recurrence frequency">
                {RECURRENCE_OPTS.map((opt) => (
                  <div key={opt.value} className="gq-form__radio-item">
                    <input
                      className="gq-form__radio"
                      type="radio"
                      name="gq-recurrence"
                      id={`gq-r-${opt.value}`}
                      checked={recurrence === opt.value}
                      onChange={() => setRecurrence(opt.value)}
                    />
                    <label className="gq-form__radio-label" htmlFor={`gq-r-${opt.value}`}>{opt.label}</label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {upgradeEligible && (
            <div className="gq-form__field">
              <label className="gq-form__checkbox-row">
                <input
                  className="gq-form__checkbox"
                  type="checkbox"
                  checked={includeDeepUpgrade}
                  onChange={(e) => setIncludeDeepUpgrade(e.target.checked)}
                />
                <span className="gq-form__checkbox-text">
                  <strong>Add deep clean upgrade</strong> — bring this visit up to full deep-clean scope.
                </span>
              </label>
            </div>
          )}

          <h2 className="gq-form__title">Your contact info</h2>

          {formMessage && (
            <div className={`gq-form__message gq-form__message--${formMessage.type}`}>{formMessage.text}</div>
          )}

          <div className="gq-form__field">
            <label className="gq-form__label" htmlFor="gq-name">Your name</label>
            <input
              id="gq-name"
              className="gq-form__input"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="gq-form__field">
            <label className="gq-form__label" htmlFor="gq-phone">Phone number</label>
            <input
              id="gq-phone"
              className="gq-form__input"
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
              required
            />
          </div>

          <div className="gq-form__field">
            <label className="gq-form__label" htmlFor="gq-email">Email (optional)</label>
            <input
              id="gq-email"
              className="gq-form__input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="gq-form__field">
            <label className="gq-form__label" htmlFor="gq-notes">Anything else we should know?</label>
            <textarea
              id="gq-notes"
              className="gq-form__textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Address, pets, special requests…"
            />
          </div>

          <button type="submit" className="gq-form__submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Request This Quote →'}
          </button>
        </form>

        <aside className="gq-preview" aria-live="polite">
          <p className="gq-preview__label">Live estimate</p>
          {loadingPrice && !result ? (
            <p className="gq-preview__loading">Calculating…</p>
          ) : priceError ? (
            <p className="gq-preview__error">{priceError}</p>
          ) : result ? (
            <>
              <p className="gq-preview__price">{formatCents(result.totalPrice)}</p>
              <p className="gq-preview__rate">
                ${result.rateUsed.toFixed(2)}/sqft · {result.estimatedHours.toFixed(1)} hrs estimated
              </p>
            </>
          ) : (
            <p className="gq-preview__loading">Fill in the form to see your price.</p>
          )}
        </aside>
      </div>
    </div>
  );
}
